import React, { Component } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import CountUp from "react-countup";

import { GET, loading, NavbarAdmin, t } from "../../components/CustomComponent.js";
import { initials } from "../../components/people_option.js";
import { withRouter } from "../../components/withRouter.js";
import "../../assets/css/dashboard.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Filler, Tooltip);
ChartJS.defaults.font.family = "FCIconic, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/* ============================================================================
 * Design tokens — theme แบรนด์ Munera (ink + lime) ชุดเดียวกับหน้า login
 * STATUS    : ใช้เฉพาะสถานะทรัพย์ ผ่านการตรวจ contrast + ตาบอดสีแล้ว
 * TYPE_RAMP : ไล่เฉด lime 8 ระดับ สำหรับสัดส่วนตามประเภททรัพย์
 * ========================================================================== */
const BRAND = {
  ink: "#142016",
  lime: "#a3e635",
  limeSoft: "#eaf9d2",
  line: "#e6eae7",
  muted: "#8b9491",
  track: "#eef1ec",
};

/* ช่องสถานะของแดชบอร์ด — ชุดเดียวกับหน้ารายงาน (STATUS ใน components/report/ReportKit.js)
   reserved รวม "อยู่ระหว่างดำเนินการ" ไว้ด้วย ส่วน closed = ขายแล้ว + ปล่อยเช่าแล้ว */
const STATUS = {
  available: "#16a34a",
  reserved: "#ca8a04",
  closed: "#dc2626",
  off_market: "#111827",
};

const STATUS_TINT = {
  available: "#dcfce7",
  reserved: "#fef9c3",
  closed: "#fee2e2",
  off_market: "#e5e7eb",
};

const TYPE_RAMP = ["#1a2e05", "#365314", "#4d7c0f", "#65a30d", "#84cc16", "#a3e635", "#bef264", "#d9f99d"];

/* ============================================================================
 * ข้อมูลทั้งหน้ามาจาก GET admin/dashboard?start=YYYY-MM-DD&end=YYYY-MM-DD
 * ไม่มีค่าจำลองเหลืออยู่แล้ว — ตัวเลขทุกตัวคำนวณจากตาราง assets / users จริง
 *
 * ส่วนที่เป็น "สต๊อก ณ ปัจจุบัน" (พอร์ตทรัพย์ ประเภททรัพย์ อันดับผู้ลงประกาศ
 * ทรัพย์ยอดนิยม งานค้าง) ไม่ขึ้นกับตัวกรองช่วงเวลา ส่วนที่เหลือขึ้นกับช่วงที่เลือก
 * ========================================================================== */

const PERIODS = [
  { key: "today", label: "period_today", report: "today" },
  { key: "d7", label: "period_7d", report: "this_week" },
  { key: "month", label: "period_this_month", report: "this_month" },
  { key: "year", label: "period_this_year", report: "this_year" },
];

/* ไอคอนของการ์ดเป็นเรื่องการแสดงผล จึงเก็บไว้ฝั่งหน้าเว็บ ไม่ให้ API ส่งมาด้วย
   key ต้องตรงกับที่ backend ส่งมา (modules/admin/dashboard/dashboard.service.js) */
const PERF_ICON = {
  listing_views_count: "\uf06e",
  published_listings: "\uf14a",
  reserved_in_period: "\uf02e",
  closed_deals: "\uf2b5",
  rental_income: "\uf53a",
  commission: "\uf295",
};

const TASK_ICON = {
  task_stale_listings: "\uf017",
  task_reserved_pending: "\uf2b5",
  task_draft_listings: "\uf15c",
  task_missing_photo: "\uf03e",
};

const REPORT_ICON = {
  today: "\uf783",
  this_week: "\uf784",
  this_month: "\uf133",
  this_year: "\uf073",
};

/* โครงเปล่าที่ใช้ระหว่างรอ API ตอบ — ทุกส่วนของหน้าจึง render ได้โดยไม่ต้องเช็ก null ทีละจุด */
const EMPTY_DATA = {
  portfolio: { total: 0, available: 0, reserved: 0, closed: 0, off_market: 0, new_listings: 0 },
  by_type: [],
  listing_report: [],
  top_listers: [],
  tasks: [],
  performance: [],
  /* null = ผู้ใช้คนนี้ไม่มีสิทธิ์เห็นตัวเลขรายได้ (backend ตัดให้ตั้งแต่ต้นทาง)
     ตั้งต้นเป็น null เพื่อไม่ให้การ์ด "รายได้" โผล่ค้างว่างระหว่างรอ API ตอบ */
  revenue: null,
  chart: { unit: "day", points: [] },
  top_viewed: [],
};

/* ---------------------------------------------------------------- วันที่ */
const TH_MONTH_SHORT = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const TH_MONTH_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const TH_DOW = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const EN_MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EN_MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EN_DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";
const monthShort = (i) => (isTh() ? TH_MONTH_SHORT[i] : EN_MONTH_SHORT[i]);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const fmtDate = (d) =>
  !d ? "" : isTh()
    ? `${d.getDate()} ${TH_MONTH_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`
    : `${d.getDate()} ${EN_MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;

const fmtRange = (s, e) => (!s ? "-" : !e || sameDay(s, e) ? fmtDate(s) : `${fmtDate(s)} - ${fmtDate(e)}`);

const fmtMonthTitle = (d) =>
  isTh()
    ? `${TH_MONTH_FULL[d.getMonth()]} ${d.getFullYear() + 543}`
    : `${EN_MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`;

// รูปแบบที่ API รับ — ประกอบเองจากวัน/เดือน/ปีที่ผู้ใช้เห็น ไม่ใช้ toISOString()
// ที่แปลงเป็น UTC แล้วทำให้ช่วง "วันนี้" เลื่อนไปหนึ่งวัน
const toISODate = (d) =>
  !d ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// ป้ายกำกับแกนเวลาของกราฟ — API ส่งจุดมาเป็นเวลา ISO พร้อมหน่วยของแกน
// (hour | day | week | month) แล้วให้หน้าเว็บจัดรูปแบบตามภาษาที่ผู้ใช้เลือก
const seriesLabel = (value, unit) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  if (unit === "hour") return `${String(d.getHours()).padStart(2, "0")}:00`;
  if (unit === "month") return monthShort(d.getMonth());
  if (unit === "week") return `${d.getDate()} ${monthShort(d.getMonth())}`;
  return String(d.getDate());
};

// ชื่อประเภททรัพย์ที่ API ส่งมาทั้งไทยและอังกฤษ — เลือกตามภาษาที่ใช้อยู่
const localName = (row) => (row ? (isTh() ? row.name_th : row.name_en || row.name_th) : "-");

// ค่าสูงสุดของชุดข้อมูล ใช้เป็นตัวหารของแถบสัดส่วน — รายการว่างต้องได้ 0 ไม่ใช่ -Infinity
const maxOf = (rows = [], pick = (x) => x) => rows.reduce((max, row) => Math.max(max, Number(pick(row)) || 0), 0);

// ช่วงวันที่ของแต่ละปุ่ม preset — อ้างอิงวันปัจจุบันจริง ไม่ hardcode
const presetRange = (key) => {
  const today = startOfDay(new Date());
  switch (key) {
    case "today": return { start: today, end: today };
    case "d7": return { start: addDays(today, -6), end: today };
    case "year": return { start: new Date(today.getFullYear(), 0, 1), end: today };
    case "month":
    default: return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
  }
};

/* ========================================================================== */

/* ============================================================================
 * Chart plugins — เลเยอร์ตกแต่งกราฟเส้น วาดเองบน canvas
 * เรียงลำดับการวาด: เส้นเฉลี่ย -> เงาใต้เส้น -> จุดสูงสุด -> เส้นตั้งตามเมาส์
 * ========================================================================== */
const roundRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

// เงานุ่ม ๆ ใต้เส้น ทำให้เส้นดูลอยเหนือพื้นที่ระบาย
const lineGlowPlugin = {
  id: "mnLineGlow",
  beforeDatasetDraw(chart, args) {
    if (args.index !== 0) return;
    const { ctx } = chart;
    ctx.save();
    ctx.shadowColor = "rgba(77, 124, 15, 0.26)";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 6;
  },
  afterDatasetDraw(chart, args) {
    if (args.index !== 0) return;
    chart.ctx.restore();
  },
};

// เส้นค่าเฉลี่ยแบบประ พร้อมป้ายกำกับมุมขวา
const avgLinePlugin = {
  id: "mnAvgLine",
  afterDatasetsDraw(chart) {
    const values = chart.data.datasets?.[0]?.data || [];
    if (values.length < 2) return;
    const { ctx, chartArea, scales } = chart;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const y = scales.y.getPixelForValue(avg);
    if (!Number.isFinite(y)) return;

    ctx.save();
    ctx.setLineDash([3, 5]);
    ctx.strokeStyle = "rgba(20,32,22,.22)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.setLineDash([]);

    const label = `${t("average")} ${Math.round(avg * 10) / 10}`;
    ctx.font = "600 10px FCIconic, sans-serif";
    const w = ctx.measureText(label).width + 16;
    const x = chartArea.right - w;
    ctx.fillStyle = "rgba(20,32,22,.86)";
    roundRectPath(ctx, x, y - 9, w, 18, 9);
    ctx.fill();
    ctx.fillStyle = "#eaf9d2";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + w / 2, y + 0.5);
    ctx.restore();
  },
};

// ไฮไลต์จุดสูงสุดไว้ตลอด — direct label แทนการใส่ตัวเลขทุกจุด
const peakMarkerPlugin = {
  id: "mnPeakMarker",
  afterDatasetsDraw(chart) {
    const values = chart.data.datasets?.[0]?.data || [];
    const meta = chart.getDatasetMeta(0);
    if (values.length < 2 || !meta?.data?.length) return;
    let mi = 0;
    values.forEach((v, i) => {
      if (v > values[mi]) mi = i;
    });
    const pt = meta.data[mi];
    if (!pt) return;

    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 10, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(163,230,53,.3)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = "#a3e635";
    ctx.strokeStyle = "#3f6212";
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();

    const label = String(values[mi]);
    ctx.font = "800 11px FCIconic, sans-serif";
    const w = ctx.measureText(label).width + 16;
    const x = Math.min(Math.max(pt.x - w / 2, chart.chartArea.left), chart.chartArea.right - w);
    const y = pt.y - 30;
    ctx.fillStyle = "#142016";
    roundRectPath(ctx, x, y, w, 19, 9.5);
    ctx.fill();
    ctx.fillStyle = "#a3e635";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + w / 2, y + 10);
    ctx.restore();
  },
};

// เส้นตั้งตามตำแหน่งเมาส์ ช่วยอ่านค่าตรงคอลัมน์
const crosshairPlugin = {
  id: "mnCrosshair",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() || [];
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = "rgba(20,32,22,.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const CHART_LINE = "#4d7c0f";

const CHART_PLUGINS = [avgLinePlugin, lineGlowPlugin, peakMarkerPlugin, crosshairPlugin];

/* ========================================================================== */

const num = (n) => Number(n || 0).toLocaleString("en-US");
const share = (part, whole) => (!whole ? 0 : Math.round((Number(part) / Number(whole)) * 100));
// โทนสีของการ์ดงานที่ต้องทำ: color = ไอคอน+ตัวเลข+แถบ, tint = พื้นไอคอน, edge = ขอบการ์ด
const TONES = {
  reserved: { color: "#ca8a04", tint: "#fef9c3", edge: "rgba(202,138,4,.18)" },
  brand: { color: "#4d7c0f", tint: "#e9f6d2", edge: "rgba(77,124,15,.16)" },
  closed: { color: "#dc2626", tint: "#fee2e2", edge: "rgba(220,38,38,.16)" },
  available: { color: "#16a34a", tint: "#dcfce7", edge: "rgba(22,163,74,.16)" },
};
const tone = (key) => TONES[key] || TONES.brand;

// ตัวเลขวิ่งขึ้นตอนโหลดและตอนเปลี่ยนตัวกรอง — ทำให้เห็นชัดว่าค่าอัปเดตแล้ว
const Figure = ({ value, prefix = "", className = "", style }) => (
  <span className={`dash-figure ${className}`} style={style}>
    <CountUp end={Number(value) || 0} duration={1.1} separator="," prefix={prefix} preserveValue />
  </span>
);

class Dashboard extends Component {
  constructor(props) {
    super(props);
    const initial = presetRange("month");
    this.state = {
      period: "month",
      range_start: initial.start,
      range_end: initial.end,
      cal_month: new Date(initial.end.getFullYear(), initial.end.getMonth(), 1),
      hover_date: null,
      range_open: false,
      data: EMPTY_DATA,
    };
    this.rangeRef = React.createRef();
  }

  async componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEsc);
    await this.getData();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEsc);
  }

  handleOutsideClick = (e) => {
    if (this.state.range_open && this.rangeRef.current && !this.rangeRef.current.contains(e.target)) {
      this.closeRange();
    }
  };

  handleEsc = (e) => {
    if (e.key === "Escape" && this.state.range_open) this.closeRange();
  };

  openRange = () => {
    const { period, range_start, range_end } = this.state;
    const focus = range_end || range_start || new Date();
    this.snapshot = { period, range_start, range_end };
    this.setState({ range_open: true, cal_month: new Date(focus.getFullYear(), focus.getMonth(), 1) });
  };

  // ปิดโดยที่ยังเลือกไม่ครบ 2 วัน = ยกเลิก กลับไปใช้ช่วงเดิม
  closeRange = () => {
    const revert = !this.state.range_end && this.snapshot ? this.snapshot : {};
    this.setState({ ...revert, range_open: false, hover_date: null });
  };

  /* backend รับช่วงวันที่ตรง ๆ จึงไม่ต้องจับคู่กับชุดข้อมูลสำเร็จรูปแบบเดิม
     ระหว่างเลือกวันสิ้นสุดยังไม่ครบ ให้ใช้วันเริ่มเป็นวันสิ้นสุดไปก่อน */
  getData = async () => {
    const { range_start, range_end } = this.state;

    loading(true);
    const result = await GET("admin/dashboard", {
      start: toISODate(range_start),
      end: toISODate(range_end || range_start),
    });
    loading(false);

    if (!result?.status || !result.data) return;

    // เติมคีย์ที่ขาดด้วยโครงเปล่า เผื่อ API เวอร์ชันเก่ายังไม่ส่งบางส่วนมา
    this.setState({ data: { ...EMPTY_DATA, ...result.data } });
  };

  changePeriod = (period) => {
    const r = presetRange(period);
    this.setState(
      {
        period,
        range_start: r.start,
        range_end: r.end,
        cal_month: new Date(r.end.getFullYear(), r.end.getMonth(), 1),
        hover_date: null,
        range_open: false,
      },
      this.getData
    );
  };

  activePeriod = () => PERIODS.find((p) => p.key === this.state.period) || null;

  // ป้ายกำกับช่วงเวลาที่ใช้ทั่วหน้า — preset ใช้ชื่อปุ่ม, custom ใช้ช่วงวันที่จริง
  periodLabel = () => {
    const p = this.activePeriod();
    return p ? t(p.label) : fmtRange(this.state.range_start, this.state.range_end);
  };

  // ป้ายสั้นสำหรับ badge ในการ์ด — ช่วงที่เลือกเองใช้คำว่า "ช่วงที่เลือก" ไม่ยัดวันที่ยาว ๆ
  periodShort = () => {
    const p = this.activePeriod();
    return p ? t(p.label) : t("custom_range");
  };

  rangeText = () => fmtRange(this.state.range_start, this.state.range_end);

  /* ------------------------------------------------------------- ปฏิทิน */

  shiftMonth = (n) => {
    const { cal_month } = this.state;
    this.setState({ cal_month: new Date(cal_month.getFullYear(), cal_month.getMonth() + n, 1) });
  };

  monthCells = (base) => {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const cells = new Array(first.getDay()).fill(null);
    const d = new Date(first);
    while (d.getMonth() === first.getMonth()) {
      cells.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return cells;
  };

  // คลิกแรก = วันเริ่ม, คลิกที่สอง = วันสิ้นสุด (คลิกย้อนหลังจะสลับให้อัตโนมัติ)
  pickDate = (date) => {
    const { range_start, range_end } = this.state;
    if (!range_start || range_end) {
      this.setState({ period: "custom", range_start: date, range_end: null, hover_date: null });
      return;
    }
    const [start, end] = date < range_start ? [date, range_start] : [range_start, date];
    this.setState({ period: "custom", range_start: start, range_end: end, hover_date: null, range_open: false }, this.getData);
  };

  go = (link) => this.props.router.navigate(link);

  /* ลิงก์ไปหน้ารายงานทรัพย์พร้อมช่วงเวลาที่กรองอยู่
     ปุ่ม preset ส่งไปเป็นชื่อช่วง เพื่อให้หน้าปลายทางไฮไลต์ปุ่มเดียวกันได้
     ช่วงที่ผู้ใช้เลือกเองส่งเป็นวันที่จริง */
  reportLink = () => {
    const p = this.activePeriod();
    const query = p
      ? `period=${p.key}`
      : `start=${toISODate(this.state.range_start)}&end=${toISODate(this.state.range_end || this.state.range_start)}`;
    return `/ad/reports/property?${query}`;
  };

  /* ------------------------------------------------------------- primitives */

  card = (children, extra = "") => <div className={`dash-card h-100 ${extra}`}>{children}</div>;

  section = (title, note) => (
    <div className="d-flex align-items-center gap-3 mb-3 mt-4">
      <span className="dash-section__title">
        <span className="dash-section__dot" />
        <span className="fw-bold text-dark text-16 text-nowrap">{title}</span>
      </span>
      {note && <span className="text-12 text-secondary text-nowrap">{note}</span>}
      <div className="dash-section__rule flex-grow-1" />
    </div>
  );

  cardTitle = (title, right = null) => (
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
      <h6 className="fw-bold mb-0 text-dark text-14">{title}</h6>
      {right}
    </div>
  );

  delta = (value) => {
    const up = Number(value) >= 0;
    return (
      <span className={`dash-delta ${up ? "dash-delta--up" : "dash-delta--down"}`}>
        <span className="icon text-10">{up ? "" : ""}</span>
        {Math.abs(Number(value)).toFixed(1)}%
      </span>
    );
  };

  bar = (value, max, color = BRAND.ink, height = 6) => (
    <div className="dash-track w-100" style={{ height: `${height}px` }}>
      <div
        className="dash-fill"
        style={{ height: `${height}px`, width: `${share(value, max)}%`, backgroundColor: color, minWidth: value ? "4px" : 0 }}
      />
    </div>
  );

  chip = (icon, color, tint, size = 40) => (
    <span
      className="dash-chip icon flex-shrink-0"
      style={{ width: `${size}px`, height: `${size}px`, backgroundColor: tint, color, fontSize: `${Math.round(size * 0.42)}px` }}
    >
      {icon}
    </span>
  );

  /* -------------------------------------------------------------- ตัวกรอง */

  renderFilter = () => {
    const { period, range_open } = this.state;
    return (
      <div className="d-flex flex-wrap align-items-center gap-3">
        <div className="dash-seg" role="group" aria-label={t("select_period")}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={p.key === period}
              className={`dash-seg__btn ${p.key === period ? "is-active" : ""}`}
              onClick={() => this.changePeriod(p.key)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>

        <div className="position-relative" ref={this.rangeRef}>
          <button
            type="button"
            className={`dash-range ${range_open ? "is-open" : ""}`}
            aria-expanded={range_open}
            aria-label={t("select_date_range")}
            onClick={() => (range_open ? this.closeRange() : this.openRange())}
          >
            <span className="icon text-14" style={{ color: BRAND.muted }}>{""}</span>
            <span className="fw-semibold">{this.rangeText()}</span>
            <span className="dash-range__chevron icon text-10">{""}</span>
          </button>

          {range_open && this.renderCalendar()}
        </div>
      </div>
    );
  };

  renderCalendar = () => {
    const { period, cal_month, range_start, range_end, hover_date } = this.state;
    const today = startOfDay(new Date());
    const cells = this.monthCells(cal_month);
    const dow = isTh() ? TH_DOW : EN_DOW;
    const atCurrentMonth = cal_month.getFullYear() === today.getFullYear() && cal_month.getMonth() === today.getMonth();
    // ระหว่างรอคลิกวันที่สอง ให้ไฮไลต์ช่วงตามตำแหน่งเมาส์
    const previewEnd = range_end || (range_start && hover_date && hover_date > range_start ? hover_date : null);

    const spanning = !!(range_start && previewEnd && !sameDay(range_start, previewEnd));

    const dayClass = (d) => {
      const disabled = d > today;
      const isStart = sameDay(d, range_start);
      const isEnd = sameDay(d, range_end);
      const inRange = range_start && previewEnd && d > range_start && d < previewEnd;
      return [
        "dash-cal__day",
        disabled ? "is-disabled" : "",
        isStart || isEnd ? "is-edge" : "",
        inRange ? "is-in" : "",
        isStart && spanning ? "is-start" : "",
        isEnd && spanning ? "is-end" : "",
        sameDay(d, today) && !isStart && !isEnd ? "is-today" : "",
      ]
        .filter(Boolean)
        .join(" ");
    };

    return (
      <div className="dash-pop dash-pop--cal">
        <div className="dash-cal">
          {/* ตัวเลือกด่วน */}
          <div className="dash-cal__presets">
            <div className="text-10 text-secondary px-2 pb-2">{t("quick_select")}</div>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`dash-pop__item ${p.key === period ? "is-active" : ""}`}
                onClick={() => this.changePeriod(p.key)}
              >
                <span>{t(p.label)}</span>
              </button>
            ))}
          </div>

          {/* ปฏิทินเลือกช่วงวันที่ */}
          <div className="dash-cal__body">
            <div className="dash-cal__head">
              <button type="button" className="dash-cal__nav icon" aria-label={t("previous_month")} onClick={() => this.shiftMonth(-1)}>
                {""}
              </button>
              <span className="fw-bold text-14 text-dark">{fmtMonthTitle(cal_month)}</span>
              <button
                type="button"
                className="dash-cal__nav icon"
                aria-label={t("next_month")}
                disabled={atCurrentMonth}
                onClick={() => this.shiftMonth(1)}
              >
                {""}
              </button>
            </div>

            <div className="dash-cal__grid dash-cal__dow">
              {dow.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>

            <div className="dash-cal__grid" onMouseLeave={() => this.setState({ hover_date: null })}>
              {cells.map((d, i) =>
                d ? (
                  <button
                    key={i}
                    type="button"
                    className={dayClass(d)}
                    disabled={d > today}
                    onMouseEnter={() => this.setState({ hover_date: d })}
                    onClick={() => this.pickDate(d)}
                  >
                    {d.getDate()}
                  </button>
                ) : (
                  <span key={i} />
                )
              )}
            </div>

            <div className="dash-cal__foot">
              <span className="text-12 text-dark fw-semibold">
                {range_start && !range_end ? t("pick_end_date") : this.rangeText()}
              </span>
              <button type="button" className="dash-ghost-btn" onClick={() => this.changePeriod("month")}>
                {t("reset")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ================================================= 1) ภาพรวมพอร์ตทรัพย์ */

  renderPortfolioTotal = () => {
    const { portfolio } = this.state.data;
    const segs = [
      { key: "status_available", v: portfolio.available, c: STATUS.available },
      { key: "status_reserved", v: portfolio.reserved, c: STATUS.reserved },
      { key: "closed_deals", v: portfolio.closed, c: STATUS.closed },
      { key: "status_off_market", v: portfolio.off_market, c: STATUS.off_market },
    ];
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <span className="text-12 text-secondary">{t("total_properties")}</span>
          {this.chip("", BRAND.ink, BRAND.limeSoft, 38)}
        </div>
        <div className="d-flex align-items-baseline gap-2">
          <Figure value={portfolio.total} className="fw-bold text-dark" style={{ fontSize: "38px" }} />
          <span className="text-12 text-secondary">{t("unit_items")}</span>
        </div>
        <div className="mt-auto pt-3">
          {/* แถบสัดส่วนสถานะ — เว้น 2px ระหว่างช่วงให้แยกกันชัด */}
          <div className="d-flex" style={{ gap: "2px" }}>
            {segs.map((s2, i) => (
              <div
                key={i}
                title={`${t(s2.key)} ${num(s2.v)}`}
                style={{ width: `${share(s2.v, portfolio.total)}%`, height: "7px", backgroundColor: s2.c, borderRadius: "4px", minWidth: s2.v ? "5px" : 0 }}
              />
            ))}
          </div>
          <div className="text-10 text-secondary mt-2">{t("as_of_now")}</div>
        </div>
      </div>
    );
  };

  renderStatusCard = ({ label, value, total, tone, icon }) => {
    const color = STATUS[tone];
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <span className="rounded-circle me-2" style={{ width: "8px", height: "8px", backgroundColor: color }} />
            <span className="text-12 text-secondary">{label}</span>
          </div>
          {this.chip(icon, color, STATUS_TINT[tone], 38)}
        </div>
        <div className="d-flex align-items-baseline gap-2">
          <Figure value={value} className="fw-bold text-dark" style={{ fontSize: "38px" }} />
          <span className="text-12 text-secondary">{t("unit_items")}</span>
        </div>
        <div className="mt-auto pt-3">
          {this.bar(value, total, color)}
          <div className="text-10 text-secondary mt-2">
            {share(value, total)}% {t("of_portfolio")}
          </div>
        </div>
      </div>
    );
  };

  renderNewListings = () => {
    const { portfolio, chart } = this.state.data;
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <span className="text-12 text-secondary">{t("new_listings")}</span>
          {this.chip("", BRAND.ink, BRAND.limeSoft, 38)}
        </div>
        <div className="d-flex align-items-baseline gap-2">
          <Figure value={portfolio.new_listings} className="fw-bold text-dark" style={{ fontSize: "38px" }} />
          <span className="text-12 text-secondary">{t("unit_items")}</span>
        </div>
        <div className="mt-auto pt-3 d-flex align-items-end justify-content-between">
          <span className="badge rounded-pill fw-semibold text-10 px-2 py-1" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
            {this.periodShort()}
          </span>
          {this.sparkline((chart.points || []).map((p) => p.value))}
        </div>
      </div>
    );
  };

  sparkline = (values) => {
    if (!values || values.length < 2) return null;
    const w = 92;
    const h = 28;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;
    const pts = values.map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / span) * (h - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="d-block">
        <defs>
          <linearGradient id="mn-spark" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={BRAND.lime} />
            <stop offset="100%" stopColor="#4d7c0f" />
          </linearGradient>
        </defs>
        <polyline points={pts.join(" ")} fill="none" stroke="url(#mn-spark)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  /* =================================================== 2) ผลงานตามช่วงเวลา */

  renderPerformanceTile = (m) =>
    this.card(
      <div className="p-3 d-flex flex-column h-100">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="text-12 text-secondary text-truncate">{t(m.key)}</span>
          {this.chip(PERF_ICON[m.key], BRAND.ink, BRAND.limeSoft, 28)}
        </div>
        <div className="d-flex align-items-baseline gap-1">
          <Figure value={m.value} className="fw-bold text-dark" style={{ fontSize: "23px" }} />
          <span className="text-10 text-secondary">{t(m.unit)}</span>
        </div>
        <div className="mt-auto pt-2">{this.delta(m.delta)}</div>
      </div>
    );

  /* ======================================================= 3) การวิเคราะห์ */

  donut = (labels, values, colors, centerValue, centerLabel) => {
    const data = {
      labels,
      datasets: [{ data: values, backgroundColor: colors, borderColor: "#fff", borderWidth: 2, hoverOffset: 10, hoverBorderColor: "#fff" }],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      animation: { animateRotate: true, duration: 900 },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: BRAND.ink,
          padding: 12,
          cornerRadius: 10,
          displayColors: false,
          titleFont: { size: 12, weight: "normal" },
          bodyFont: { size: 14, weight: "bold" },
          callbacks: {
            label: (c) => {
              const tot = c.dataset.data.reduce((s, v) => s + v, 0);
              return `${num(c.parsed)} ${t("unit_items")} (${share(c.parsed, tot)}%)`;
            },
          },
        },
      },
    };
    return (
      <div className="position-relative mx-auto" style={{ width: "172px", height: "172px" }}>
        <Doughnut data={data} options={options} />
        <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: "none" }}>
          <Figure value={centerValue} className="fw-bold text-dark d-block" style={{ fontSize: "27px" }} />
          <div className="text-10 text-secondary">{centerLabel}</div>
        </div>
      </div>
    );
  };

  legendRow = (color, label, value, pct) => (
    <div className="dash-rank d-flex align-items-center justify-content-between py-1 px-2">
      <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
        <span className="rounded-circle me-2 flex-shrink-0" style={{ width: "9px", height: "9px", backgroundColor: color }} />
        <span className="text-12 text-dark text-truncate">{label}</span>
      </div>
      <div className="text-nowrap ms-2">
        <span className="text-12 fw-bold text-dark dash-figure">{num(value)}</span>
        <span className="text-10 text-secondary ms-2" style={{ display: "inline-block", width: "34px", textAlign: "right" }}>{pct}%</span>
      </div>
    </div>
  );

  renderTypeDonut = () => {
    const { by_type, portfolio } = this.state.data;
    const sorted = [...by_type].sort((a, b) => b.total - a.total);
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        {this.cardTitle(t("properties_by_type"))}
        {this.donut(
          sorted.map((x) => localName(x)),
          sorted.map((x) => x.total),
          sorted.map((_, i) => TYPE_RAMP[i % TYPE_RAMP.length]),
          portfolio.total,
          t("unit_items")
        )}
        <div className="mt-3">
          {sorted.map((x, i) => (
            <React.Fragment key={i}>
              {this.legendRow(TYPE_RAMP[i % TYPE_RAMP.length], localName(x), x.total, share(x.total, portfolio.total))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  renderStatusDonut = () => {
    const { portfolio } = this.state.data;
    const rows = [
      { label: t("status_available"), value: portfolio.available, color: STATUS.available },
      { label: t("status_reserved"), value: portfolio.reserved, color: STATUS.reserved },
      { label: t("closed_deals"), value: portfolio.closed, color: STATUS.closed },
      { label: t("status_off_market"), value: portfolio.off_market, color: STATUS.off_market },
    ];
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        {this.cardTitle(t("property_status"))}
        {this.donut(rows.map((r) => r.label), rows.map((r) => r.value), rows.map((r) => r.color), portfolio.total, t("unit_items"))}
        <div className="mt-3">
          {rows.map((r, i) => (
            <React.Fragment key={i}>{this.legendRow(r.color, r.label, r.value, share(r.value, portfolio.total))}</React.Fragment>
          ))}
        </div>
        <div className="mt-auto pt-3" style={{ borderTop: `1px solid ${BRAND.line}` }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-12 text-secondary">{t("close_rate")}</span>
            <span className="badge rounded-pill text-14 fw-bold px-3 py-2" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
              {share(portfolio.closed, portfolio.total)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderTrendChart = () => {
    const { chart, portfolio } = this.state.data;
    // API ส่งจุดกราฟมาเป็น [{ date, value }] พร้อมหน่วยของแกน แล้วให้หน้าเว็บจัดป้ายกำกับเอง
    const points = chart.points || [];
    const values = points.map((p) => Number(p.value) || 0);
    const labels = points.map((p) => seriesLabel(p.date, chart.unit));
    const peak = maxOf(values);
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = values.length ? Math.round((sum / values.length) * 10) / 10 : 0;

    // เทียบครึ่งหลังกับครึ่งแรกของช่วง เพื่อบอกทิศทางในหนึ่งบรรทัด
    const half = Math.floor(values.length / 2);
    const firstAvg = values.slice(0, half).reduce((a, b) => a + b, 0) / (half || 1);
    const lastAvg = values.slice(half).reduce((a, b) => a + b, 0) / (values.length - half || 1);
    const momentum = firstAvg ? ((lastAvg - firstAvg) / firstAvg) * 100 : 0;

    const data = {
      labels,
      datasets: [
        {
          label: t("new_listings"),
          data: values,
          borderColor: CHART_LINE,
          borderWidth: 1.8,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          tension: 0.4,
          // จุดประจำวัน — ย่อขนาดลงเมื่อช่วงยาวเพื่อไม่ให้เบียดกัน
          pointRadius: values.length > 20 ? 2.2 : 3.2,
          pointBackgroundColor: "#fff",
          pointBorderColor: CHART_LINE,
          pointBorderWidth: 1.5,
          pointHitRadius: 18,
          pointHoverRadius: 5.5,
          pointHoverBackgroundColor: BRAND.lime,
          pointHoverBorderColor: CHART_LINE,
          pointHoverBorderWidth: 2,
          fill: true,
          backgroundColor: (ctx) => {
            const { chart: c } = ctx;
            if (!c.chartArea) return "rgba(163,230,53,.25)";
            const g = c.ctx.createLinearGradient(0, c.chartArea.top, 0, c.chartArea.bottom);
            g.addColorStop(0, "rgba(132,204,22,.55)");
            g.addColorStop(0.35, "rgba(163,230,53,.28)");
            g.addColorStop(0.75, "rgba(163,230,53,.07)");
            g.addColorStop(1, "rgba(163,230,53,0)");
            return g;
          },
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      animation: { duration: 900, easing: "easeOutQuart" },
      layout: { padding: { top: 18, right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(20,32,22,.96)",
          padding: 14,
          cornerRadius: 12,
          displayColors: false,
          caretSize: 6,
          titleColor: "rgba(255,255,255,.6)",
          titleFont: { size: 11, weight: "normal" },
          bodyColor: BRAND.lime,
          bodyFont: { size: 16, weight: "bold" },
          callbacks: { label: (c) => `${num(c.parsed.y)} ${t("unit_items")}` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: BRAND.muted, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 18, padding: 6 },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(20,32,22,.06)", drawTicks: false },
          border: { display: false, dash: [4, 5] },
          ticks: { color: BRAND.muted, font: { size: 10 }, precision: 0, maxTicksLimit: 5, padding: 10 },
        },
      },
    };

    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        <div className="d-flex align-items-start justify-content-between mb-1 gap-2 flex-wrap">
          <div>
            <h6 className="fw-bold mb-1 text-dark text-14">{t("listing_trend_chart")}</h6>
            <div className="d-flex align-items-center gap-2">
              {this.delta(momentum)}
              <span className="text-10 text-secondary">{t("second_half_vs_first")}</span>
            </div>
          </div>
          <span className="badge rounded-pill fw-semibold text-10 px-3 py-2" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
            {this.rangeText()}
          </span>
        </div>

        <div className="flex-grow-1 mt-2" style={{ minHeight: "255px" }}>
          <Line data={data} options={options} plugins={CHART_PLUGINS} />
        </div>

        <div className="dash-stat-row mt-3 pt-3">
          {[
            { label: t("average"), value: avg, dot: BRAND.muted },
            { label: t("peak"), value: peak, dot: BRAND.lime },
            { label: t("total_in_period"), value: portfolio.new_listings, dot: BRAND.ink },
          ].map((st, i) => (
            <div className="dash-stat" key={i}>
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: st.dot }} />
                <span className="text-10 text-secondary">{st.label}</span>
              </div>
              <div className="fw-bold text-dark text-18 dash-figure mt-1">
                {st.value} <span className="text-10 text-secondary fw-normal">{t("unit_items")}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ==================================================== 4) รายงานและอันดับ */

  renderListingReport = () => {
    const activeReport = this.activePeriod()?.report;
    return this.card(
      <div className="p-4">
        {this.cardTitle(t("listing_report"))}
        <div className="d-flex flex-column">
          {this.state.data.listing_report.map((r, i) => {
            const on = r.key === activeReport;
            return (
              <button
                key={i}
                type="button"
                onClick={() => this.changePeriod(PERIODS.find((p) => p.report === r.key)?.key || "month")}
                className="dash-rank d-flex align-items-center justify-content-between border-0 w-100 text-start px-2 py-3 mb-1"
                style={{ backgroundColor: on ? BRAND.limeSoft : "transparent" }}
              >
                <div className="d-flex align-items-center">
                  {this.chip(REPORT_ICON[r.key], on ? BRAND.ink : BRAND.muted, on ? "#fff" : BRAND.track, 32)}
                  <span className={`text-14 ms-3 text-dark ${on ? "fw-bold" : ""}`}>{t(r.key)}</span>
                </div>
                <div className="text-nowrap">
                  <span className="fw-bold text-dark text-18 dash-figure">{num(r.value)}</span>
                  <span className="text-10 text-secondary ms-1">{t("unit_items")}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  renderRevenue = () => {
    const { revenue } = this.state.data;
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <h6 className="fw-bold mb-0 text-dark text-14">
            {t("revenue")}{" "}
            <span className="fw-normal text-secondary">({this.periodShort()})</span>
          </h6>
          {this.chip("", BRAND.ink, BRAND.limeSoft, 32)}
        </div>

        {[
          { label: t("rental_income"), value: revenue.rent, prefix: "฿", suffix: "" },
          { label: t("commission"), value: revenue.commission, prefix: "฿", suffix: "" },
          { label: t("deals_closed"), value: revenue.deals, prefix: "", suffix: t("unit_deals") },
        ].map((r, i) => (
          <div key={i} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: `1px solid ${BRAND.line}` }}>
            <span className="text-12 text-secondary">{r.label}</span>
            <span className="text-14 fw-bold text-dark">
              <Figure value={r.value} prefix={r.prefix} />
              {r.suffix && <span className="text-10 fw-normal text-secondary ms-1">{r.suffix}</span>}
            </span>
          </div>
        ))}

        <div className="mt-auto pt-3">
          <div
            className="rounded-3 p-3 d-flex justify-content-between align-items-center"
            style={{ backgroundColor: BRAND.limeSoft, border: "1px solid #cfe9a8" }}
          >
            <span className="text-12 fw-bold" style={{ color: BRAND.ink }}>{t("total_income")}</span>
            <Figure value={revenue.total} prefix="฿" className="fw-bold" style={{ fontSize: "21px", color: BRAND.ink }} />
          </div>
        </div>
      </div>
    );
  };

  renderTopListers = () => {
    const { top_listers } = this.state.data;
    const max = maxOf(top_listers, (x) => x.total);
    return this.card(
      <div className="p-4">
        {this.cardTitle(t("top_listers"), <span className="text-10 text-secondary">{t("cumulative_all_time")}</span>)}
        <div className="table-responsive">
          <table className="table table-borderless align-middle mb-0 dash-table">
            <thead>
              <tr className="text-secondary text-10">
                <th className="ps-0 fw-normal">{t("lister")}</th>
                <th className="text-end fw-normal">{t("total")}</th>
                <th className="text-end fw-normal">{t("status_available")}</th>
                <th className="text-end fw-normal">{t("status_reserved")}</th>
                <th className="text-end fw-normal pe-0">{t("closed_deals")}</th>
              </tr>
            </thead>
            <tbody>
              {top_listers.map((p, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${BRAND.line}` }}>
                  <td className="ps-0 py-3" style={{ minWidth: "170px" }}>
                    <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-10 me-2 flex-shrink-0"
                        style={
                          i === 0
                            ? { width: "32px", height: "32px", background: "linear-gradient(135deg,#243b28,#101a13)", color: BRAND.lime, boxShadow: "0 4px 12px -6px rgba(20,32,22,.8)" }
                            : { width: "32px", height: "32px", backgroundColor: BRAND.limeSoft, color: BRAND.ink }
                        }
                      >
                        {initials(p.name)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="text-12 text-dark text-truncate">{p.name}</div>
                        <div style={{ width: "72px", marginTop: "5px" }}>{this.bar(p.total, max, BRAND.lime, 4)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-end text-14 fw-bold text-dark dash-figure">{num(p.total)}</td>
                  <td className="text-end text-14 dash-figure" style={{ color: STATUS.available }}>{num(p.available)}</td>
                  <td className="text-end text-14 dash-figure" style={{ color: STATUS.reserved }}>{num(p.reserved)}</td>
                  <td className="text-end text-14 fw-bold pe-0 dash-figure" style={{ color: STATUS.closed }}>{num(p.closed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  renderTopViewed = () => {
    const { top_viewed } = this.state.data;
    const max = maxOf(top_viewed, (x) => x.views);
    return this.card(
      <div className="p-4 d-flex flex-column h-100">
        {this.cardTitle(
          t("top10_most_viewed"),
          /* ส่ง sort ไปกับ url ให้หน้าจัดการทรัพย์เปิดมาพร้อมเรียงตามยอดเข้าชม
             จะได้เห็นอันดับที่ 11 ต่อจากการ์ดนี้ทันที ไม่ต้องไปเลือกเรียงเอง */
          <button type="button" className="dash-ghost-btn" onClick={() => this.go("/ad/assets?sort=views_desc")}>
            {t("view_all")}
          </button>
        )}
        <div className="d-flex flex-column">
          {top_viewed.map((item, i) => (
            <div
              key={i}
              className={`dash-rank ${i < 3 ? "dash-rank--top" : ""} d-flex align-items-center px-2 py-2`}
              style={{ borderBottom: i < top_viewed.length - 1 ? `1px solid ${BRAND.line}` : "none" }}
            >
              <span
                className="dash-rank__no d-flex align-items-center justify-content-center rounded-3 text-12 me-3 flex-shrink-0"
                style={
                  i < 3
                    ? { width: "24px", height: "24px", backgroundColor: BRAND.limeSoft, color: BRAND.ink, fontWeight: 800 }
                    : { width: "24px", height: "24px", color: BRAND.muted }
                }
              >
                {i + 1}
              </span>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="text-12 text-dark text-truncate">{item.name}</div>
                <div className="text-10 text-secondary">{localName({ name_th: item.type_th, name_en: item.type_en })} · {item.zone}</div>
              </div>
              <div className="mx-3 flex-shrink-0" style={{ width: "78px" }}>{this.bar(item.views, max, BRAND.lime, 4)}</div>
              <div className="text-nowrap text-end flex-shrink-0" style={{ width: "72px" }}>
                <span className="text-14 fw-bold text-dark dash-figure">{num(item.views)}</span>
                <span className="text-10 text-secondary ms-1">{t("unit_views")}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderTypeSummary = () => {
    const { by_type, portfolio } = this.state.data;
    const max = maxOf(by_type, (x) => x.total);
    return this.card(
      <div className="p-4">
        {this.cardTitle(
          t("summary_by_type"),
          /* รายงานทรัพย์คือหน้าที่ขยายตารางนี้ต่อ (แยกตามทำเล ช่วงราคา อายุประกาศ)
             ส่งช่วงวันที่ที่กรองอยู่ไปด้วย เปิดไปแล้วจะได้เห็นช่วงเดียวกัน */
          <button type="button" className="dash-ghost-btn" onClick={() => this.go(this.reportLink())}>
            {t("view_full_report")}
          </button>
        )}
        <div className="table-responsive">
          <table className="table table-borderless align-middle mb-0 dash-table">
            <thead>
              <tr className="text-secondary text-10">
                <th className="ps-0 fw-normal" style={{ minWidth: "150px" }}>{t("property_type")}</th>
                <th className="text-end fw-normal">{t("total")}</th>
                <th className="text-end fw-normal">{t("status_available")}</th>
                <th className="text-end fw-normal">{t("status_reserved")}</th>
                <th className="text-end fw-normal">{t("closed_deals")}</th>
                <th className="text-end fw-normal pe-0" style={{ width: "115px" }}>{t("vacancy_rate")}</th>
              </tr>
            </thead>
            <tbody>
              {by_type.map((row, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${BRAND.line}` }}>
                  <td className="ps-0 py-3">
                    <div className="d-flex align-items-center">
                      <span className="rounded-circle me-2 flex-shrink-0" style={{ width: "9px", height: "9px", backgroundColor: TYPE_RAMP[i % TYPE_RAMP.length] }} />
                      <div style={{ minWidth: 0 }}>
                        <div className="text-14 text-dark">{localName(row)}</div>
                        <div style={{ width: "92px", marginTop: "5px" }}>{this.bar(row.total, max, BRAND.ink, 4)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-end text-14 fw-bold text-dark dash-figure">{num(row.total)}</td>
                  <td className="text-end text-14 dash-figure" style={{ color: STATUS.available }}>{num(row.available)}</td>
                  <td className="text-end text-14 dash-figure" style={{ color: STATUS.reserved }}>{num(row.reserved)}</td>
                  <td className="text-end text-14 dash-figure" style={{ color: STATUS.closed }}>{num(row.closed)}</td>
                  <td className="text-end pe-0">
                    <div className="d-flex align-items-center justify-content-end gap-2">
                      <div style={{ width: "46px" }}>{this.bar(row.available, row.total, BRAND.lime, 4)}</div>
                      <span className="text-12 fw-bold text-dark dash-figure" style={{ minWidth: "34px", textAlign: "right" }}>
                        {share(row.available, row.total)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${BRAND.ink}` }}>
                <td className="ps-0 pt-3 text-14 fw-bold text-dark">{t("total")}</td>
                <td className="text-end pt-3 text-14 fw-bold text-dark dash-figure">{num(portfolio.total)}</td>
                <td className="text-end pt-3 text-14 fw-bold text-dark dash-figure">{num(portfolio.available)}</td>
                <td className="text-end pt-3 text-14 fw-bold text-dark dash-figure">{num(portfolio.reserved)}</td>
                <td className="text-end pt-3 text-14 fw-bold text-dark dash-figure">{num(portfolio.closed)}</td>
                <td className="text-end pt-3 pe-0 text-14 fw-bold text-dark dash-figure">{share(portfolio.available, portfolio.total)}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  /* ============================================= 5) สิ่งที่ต้องดำเนินการ */

  // การ์ดแสดงผลอย่างเดียว ไม่ใช่ปุ่ม — sharePct = สัดส่วนของงานค้างทั้งหมด
  renderTaskTile = (task, sharePct) => {
    const tn = tone(task.tone);
    return (
      <div
        className="dash-card dash-card--tone h-100 p-4 d-flex flex-column"
        style={{ borderColor: tn.edge, "--tone": tn.color }}
      >
        <div className="d-flex align-items-center gap-3 mb-3">
          {this.chip(TASK_ICON[task.key], tn.color, tn.tint, 44)}
          <div style={{ minWidth: 0 }}>
            <div className="text-14 text-dark fw-semibold">{t(task.key)}</div>
            <div className="text-10 text-secondary">{t(task.note)}</div>
          </div>
        </div>

        <div className="mt-auto">
          <div className="d-flex align-items-baseline justify-content-between mb-2">
            <div className="d-flex align-items-baseline gap-2">
              <Figure value={task.count} className="fw-bold" style={{ fontSize: "34px", color: tn.color }} />
              <span className="text-12" style={{ color: tn.color, opacity: 0.75 }}>{t(task.unit)}</span>
            </div>
            <span className="text-14 fw-bold dash-figure" style={{ color: tn.color, opacity: 0.85 }}>{sharePct}%</span>
          </div>

          <div className="dash-track w-100" style={{ height: "6px", backgroundColor: tn.tint }}>
            <div className="dash-fill" style={{ height: "6px", width: `${sharePct}%`, backgroundColor: tn.color, minWidth: task.count ? "5px" : 0 }} />
          </div>
          <div className="text-10 text-secondary mt-2">{t("of_pending_total")}</div>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- render */

  render() {
    const { data } = this.state;
    const { portfolio } = data;
    const periodLabel = this.periodShort();
    /* ตัวเลขรายได้มาจาก API เท่านั้น — ไม่ได้ตัดสินจาก user_type ที่แคชไว้ในเครื่อง
       ทีมงานภายใน (admin) เห็นข้อมูลทั้งบริษัท backend จึงไม่ส่งรายได้มาให้
       (ดู modules/admin/dashboard/dashboard.service.js) การ์ดและ 2 ใบในผลงานจึงหายไปพร้อมกัน */
    const showRevenue = !!data.revenue;
    // ผลงานเหลือ 4 ใบเมื่อไม่มีรายได้ — ขยายการ์ดให้เต็มแถวแทนที่จะทิ้งช่องว่างไว้
    const perfCol = data.performance.length > 4 ? "col-xl-2" : "col-xl-3";
    const stagger = (i) => ({ animationDelay: `${Math.min(i, 8) * 45}ms` });
    const taskTotal = data.tasks.reduce((sum, x) => sum + x.count, 0);

    return (
      <div className="w-100 min-vh-100 dash-page">
        <NavbarAdmin />
        <div className="body p-4">

          {/* Header + ตัวกรองช่วงเวลา */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("dashboard")}</h3>
              <div className="text-secondary text-14">{t("dashboard_subtitle")}</div>
            </div>
            {this.renderFilter()}
          </div>

          {/* ---------------- 1) ภาพรวมพอร์ตทรัพย์ ---------------- */}
          {this.section(t("section_portfolio_overview"), t("as_of_now"))}
          <div className="row g-3">
            <div className="col-xl col-lg-4 col-md-6 dash-in" style={stagger(0)}>{this.renderPortfolioTotal()}</div>
            <div className="col-xl col-lg-4 col-md-6 dash-in" style={stagger(1)}>
              {this.renderStatusCard({ label: t("status_available"), value: portfolio.available, total: portfolio.total, tone: "available", icon: "" })}
            </div>
            <div className="col-xl col-lg-4 col-md-6 dash-in" style={stagger(2)}>
              {this.renderStatusCard({ label: t("status_reserved"), value: portfolio.reserved, total: portfolio.total, tone: "reserved", icon: "" })}
            </div>
            <div className="col-xl col-lg-6 col-md-6 dash-in" style={stagger(3)}>
              {this.renderStatusCard({ label: t("closed_deals"), value: portfolio.closed, total: portfolio.total, tone: "closed", icon: "" })}
            </div>
            <div className="col-xl col-lg-6 col-md-12 dash-in" style={stagger(4)}>{this.renderNewListings()}</div>
          </div>

          {/* ---------------- 2) ผลงาน ---------------- */}
          {this.section(t("section_performance"), `${periodLabel} · ${t("vs_previous_period")}`)}
          <div className="row g-3">
            {data.performance.map((m, i) => (
              <div className={`${perfCol} col-lg-4 col-md-6 dash-in`} style={stagger(i)} key={m.key}>
                {this.renderPerformanceTile(m)}
              </div>
            ))}
          </div>

          {/* ---------------- 3) การวิเคราะห์ ---------------- */}
          {this.section(t("section_analytics"))}
          <div className="row g-3">
            <div className="col-xl-3 col-lg-6 dash-in" style={stagger(0)}>{this.renderTypeDonut()}</div>
            <div className="col-xl-6 col-lg-12 order-xl-0 order-lg-last dash-in" style={stagger(1)}>{this.renderTrendChart()}</div>
            <div className="col-xl-3 col-lg-6 dash-in" style={stagger(2)}>{this.renderStatusDonut()}</div>
          </div>

          {/* ---------------- 4) รายงานและอันดับ ---------------- */}
          {this.section(t("section_reports_ranking"))}
          <div className="row g-3">
            <div className="col-xl-3 col-lg-6 dash-in" style={stagger(0)}>{this.renderListingReport()}</div>
            {showRevenue && (
              <div className="col-xl-3 col-lg-6 dash-in" style={stagger(1)}>{this.renderRevenue()}</div>
            )}
            <div className={`${showRevenue ? "col-xl-6" : "col-xl-9"} col-lg-12 dash-in`} style={stagger(2)}>
              {this.renderTopListers()}
            </div>

            <div className="col-xl-5 col-lg-12 dash-in" style={stagger(3)}>{this.renderTopViewed()}</div>
            <div className="col-xl-7 col-lg-12 dash-in" style={stagger(4)}>{this.renderTypeSummary()}</div>
          </div>

          {/* ---------------- 5) สิ่งที่ต้องดำเนินการ ---------------- */}
          {this.section(t("section_action_items"))}
          <div className="row g-3 pb-4">
            {data.tasks.map((task, i) => (
              <div className="col-xl-3 col-md-6 dash-in" style={stagger(i)} key={task.key}>
                {this.renderTaskTile(task, share(task.count, taskTotal))}
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  }
}

export default withRouter(Dashboard);
